import { Injectable, inject, signal, ApplicationRef } from '@angular/core';
import { BehaviorSubject, firstValueFrom, of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { NEBULA_UI_CONFIG } from 'nebula-ui-kit';
import { HttpService } from '../http/http.service';

/** Idioma disponible (segun simappe-admin). */
export interface AppLanguage {
  id: number;
  code: string;
  name: string;
  nativeName: string;
  flagEmoji?: string;
  isActive: boolean;
  sortOrder?: number;
}

/** Bundle de traducciones (plano: clave con puntos -> valor) que entrega el backend. */
interface TranslationBundle {
  version: string;
  language: string;
  generatedAt?: string;
  translations: Record<string, string>;
}

interface CachedBundle extends TranslationBundle {
  cachedAt: number;
}

/**
 * i18n del frontend (mismo patron de Electo/simappe_admin): carga el bundle de traducciones por
 * idioma desde simappe-admin (`/translation/bundle/{lang}`), lo cachea en localStorage con TTL y lo
 * resuelve sincronicamente via {@link translate}. El cambio de idioma fuerza re-render (OnPush)
 * notificando por {@link currentLang$}. Es 100% del lado admin: no requiere endpoints nuevos.
 */
@Injectable({ providedIn: 'root' })
export class TranslationService {
  private readonly http = inject(HttpService);
  private readonly appRef = inject(ApplicationRef);
  /** Config del kit (mutable): se le ajusta `locale` por idioma para fechas/numeros (formatDate). */
  private readonly uiConfig = inject(NEBULA_UI_CONFIG, { optional: true }) as {
    locale?: string;
  } | null;

  /** Mapa codigo i18n -> locale BCP-47 para el formateo del kit. */
  private readonly LOCALE_MAP: Record<string, string> = { es: 'es-CO', en: 'en-US' };

  /** Aplica el locale del idioma activo al config del kit (fechas/numeros app-wide). */
  private applyLocale(code: string): void {
    if (this.uiConfig) this.uiConfig.locale = this.LOCALE_MAP[code] ?? this.uiConfig.locale;
  }

  private readonly base = '/simappe-admin/api/v1/translation';

  /**
   * Sello de esta carga de la aplicacion, que viaja como parametro del bundle.
   *
   * El endpoint se sirve con `Cache-Control: max-age=86400`, de modo que el
   * navegador reusa su copia un dia entero sin salir a la red: las claves
   * publicadas despues quedaban invisibles aunque el backend ya las sirviera.
   * Revalidar con `Cache-Control: no-cache` no es viable porque ese encabezado
   * no esta en la lista CORS del servicio y la peticion no saldria.
   *
   * Con un sello por arranque, la URL cambia en cada carga de la pagina (va a
   * la red y trae lo publicado) pero se repite dentro de la misma sesion, asi
   * que alternar entre idiomas ya cargados sigue saliendo de la cache.
   */
  private readonly sessionStamp = Date.now().toString(36);
  private readonly CACHE_PREFIX = 'nebula_i18n_bundle_';
  private readonly CACHE_TS_SUFFIX = '_ts';
  private readonly CACHE_TTL_MS = 24 * 60 * 60 * 1000; // 1 dia

  /** Traducciones planas en memoria por idioma. */
  private readonly dict = new Map<string, Record<string, string>>();

  private readonly currentLangSubject = new BehaviorSubject<string>('es');
  /** Idioma activo (observable, para que el pipe re-renderice al cambiar). */
  readonly currentLang$ = this.currentLangSubject.asObservable();
  /** Idioma activo como signal (para que los `computed` reaccionen al cambio). */
  readonly lang = signal<string>('es');

  /** Idiomas disponibles (activos). */
  readonly availableLanguages = signal<AppLanguage[]>([]);

  /** Idioma activo (sincrono). */
  get currentLang(): string {
    return this.currentLangSubject.value;
  }

  /**
   * Inicializa el i18n: idiomas disponibles + bundle del idioma guardado (o 'es' por defecto).
   * Pensado para invocarse desde un APP_INITIALIZER (espera el bundle antes del primer render).
   */
  async init(): Promise<void> {
    const saved =
      (typeof localStorage !== 'undefined' && localStorage.getItem('selectedLanguage')) || 'es';
    // Mismo orden que en setLanguage: primero el signal, despues el aviso.
    this.lang.set(saved);
    this.currentLangSubject.next(saved);
    this.applyLocale(saved);
    await Promise.all([this.loadLanguages(), this.loadBundle(saved)]);
  }

  /** Cambia el idioma activo (recargando su bundle) y fuerza re-render. */
  async setLanguage(code: string): Promise<void> {
    // Siempre se recarga, tambien al volver a un idioma ya visitado: de lo
    // contrario el idioma con el que arranco la sesion se quedaba congelado
    // con el bundle de entonces y las claves publicadas despues nunca salian.
    // El costo es bajo porque {@link sessionStamp} deja que el navegador
    // resuelva de su cache los bundles ya pedidos en esta misma carga.
    await this.loadBundle(code);
    if (typeof localStorage !== 'undefined') {
      localStorage.setItem('selectedLanguage', code);
    }
    // El signal se actualiza ANTES de avisar por el observable: quien despierta
    // con `currentLang$` suele leer `lang()` para recalcular, y al reves lo leia
    // todavia con el idioma anterior. Asi el titulo del navegador se quedaba un
    // idioma por detras: en ingles mostraba el texto en español y viceversa.
    this.lang.set(code);
    this.currentLangSubject.next(code);
    this.applyLocale(code);
    this.appRef.tick();
  }

  /**
   * Resuelve una clave a su texto en el idioma activo. Interpola tanto {{nombre}} como {0}/{1}
   * (convencion del backend). Fallback: la clave.
   */
  translate(key: string, params?: Record<string, unknown>): string {
    if (!key) return '';
    const value = this.dict.get(this.currentLang)?.[key];
    if (value == null) return key;
    if (!params) return value;
    return value
      .replace(/{{\s*(\w+)\s*}}/g, (_, p) => String(params[p] ?? ''))
      .replace(/{(\d+)}/g, (m, i) => (params[i] != null ? String(params[i]) : m));
  }

  /** Idiomas habilitados en Nebula (por ahora solo espanol e ingles). */
  private readonly ENABLED_LANGS = ['es', 'en'];

  /** Banderas forzadas en el front: es -> Colombia (no España), en -> EE.UU. (America, no Reino Unido). */
  private readonly FLAG_OVERRIDE: Record<string, string> = { es: '🇨🇴', en: '🇺🇸' };

  private async loadLanguages(): Promise<void> {
    const langs = await firstValueFrom(
      this.http
        .getContent<AppLanguage[]>(`${this.base}/languages`)
        .pipe(catchError(() => of([] as AppLanguage[]))),
    );
    this.availableLanguages.set(
      (langs || [])
        .filter((l) => l.isActive && this.ENABLED_LANGS.includes(l.code))
        .map((l) => ({ ...l, flagEmoji: this.FLAG_OVERRIDE[l.code] ?? l.flagEmoji })),
    );
  }

  private async loadBundle(code: string): Promise<void> {
    // Network-first: siempre se intenta traer el bundle fresco del backend (asi las claves nuevas
    // se reflejan de inmediato tras un deploy/registro). El cache local es solo fallback offline.
    const fetched = await this.fetchAndCache(code);
    if (!fetched) {
      const cached = this.readCache(code);
      if (cached) this.dict.set(code, cached.translations);
    }
  }

  private async fetchAndCache(code: string): Promise<boolean> {
    const bundle = await firstValueFrom(
      this.http
        .getContent<TranslationBundle>(`${this.base}/bundle/${code}`, { v: this.sessionStamp })
        .pipe(catchError(() => of(null as unknown as TranslationBundle))),
    );
    if (bundle?.translations) {
      this.dict.set(code, bundle.translations);
      this.writeCache(code, bundle);
      return true;
    }
    return false;
  }

  private readCache(code: string): CachedBundle | null {
    if (typeof localStorage === 'undefined') return null;
    try {
      const raw = localStorage.getItem(this.CACHE_PREFIX + code);
      const ts = localStorage.getItem(this.CACHE_PREFIX + code + this.CACHE_TS_SUFFIX);
      if (!raw || !ts) return null;
      if (Date.now() - parseInt(ts, 10) > this.CACHE_TTL_MS) return null;
      return JSON.parse(raw) as CachedBundle;
    } catch {
      return null;
    }
  }

  private writeCache(code: string, bundle: TranslationBundle): void {
    if (typeof localStorage === 'undefined') return;
    try {
      const cached: CachedBundle = { ...bundle, cachedAt: Date.now() };
      localStorage.setItem(this.CACHE_PREFIX + code, JSON.stringify(cached));
      localStorage.setItem(this.CACHE_PREFIX + code + this.CACHE_TS_SUFFIX, Date.now().toString());
    } catch {
      /* cuota de localStorage: ignorar, queda solo en memoria */
    }
  }
}
